import { FC, useMemo } from 'react'
import { ButtonTransformer } from 'src/relay/Transformers/ButtonTransormer'
import {
  ImageCaption,
  ImageTransformer,
  unwrapMedia,
} from 'src/relay/Transformers/ImageTransformer'
import AccordionList, { AccordionListProps } from 'src/components/AccordionList'
import { YOUTUBE_OPTIONS } from 'src/constant'
import { RichTextTransformer } from 'src/relay/Transformers/RichTextTransformer'
import { useTranslation } from 'react-i18next'
import { getThemeColor } from 'src/relay/Transformers/ThemeTransformer'

import { BlockProps } from '../props'

import { AccordeonContentProps } from './props'

const AccordeonContent: FC<BlockProps<AccordeonContentProps>> = ({
  data,
  configuration,
  locale,
}) => {
  const { t } = useTranslation()
  const props: AccordionListProps = useMemo(
    () => ({
      ...(data.title && {
        blockTitleProps: {
          ...(data.sectionTitle && {
            tag: data.sectionTitle,
          }),
          title: data.title,
          htmlTag: 'hgroup',
          color: getThemeColor(data.theme?.data),
        },
      }),
      accordions: data?.accordeonEntry?.length
        ? data.accordeonEntry?.map((item) => {
            const desktopImage = unwrapMedia(item?.desktopImage)
            const mobileImage = unwrapMedia(item?.mobileImage)
            const hasImage = Boolean(desktopImage)

            return {
              title: item.title || '',
              ...(item.description && {
                richTextProps: RichTextTransformer(
                  item.description,
                  configuration,
                  locale
                ),
              }),
              ...(hasImage && {
                media: {
                  caption:
                    ImageCaption(desktopImage) || ImageCaption(mobileImage),
                  pictureProps: {
                    imageProps: ImageTransformer(desktopImage),
                    mobileImageProps: ImageTransformer(mobileImage),
                  },
                },
              }),
              ...(item?.videoId && {
                media: {
                  options: {
                    ...YOUTUBE_OPTIONS,
                    videoId: item.videoId,
                  },
                  texts: {
                    play: t('video.play'),
                    playVideo: t('video.playVideo'),
                    pause: t('video.pause'),
                    skip: t('video.skip'),
                    mute: t('video.mute'),
                    unmute: t('video.unmute'),
                    transcript: t('video.transcript'),
                    fullscreen: t('video.fullscreen'),
                    exitFullscreen: t('video.exitFullscreen'),
                  },
                },
              }),
              ...(item.cta && {
                actionButtons: item.cta.url
                  ? [ButtonTransformer({ data: item.cta })].filter(
                      (button) => !!button
                    )
                  : undefined,
              }),
            }
          })
        : [],
    }),
    [configuration, data, locale, t]
  )

  return <AccordionList {...props} />
}

export default AccordeonContent
